"use client"

import { DiscordIcon } from "@/app/icons/discord"
import { InstagramIcon } from "@/app/icons/instagram"
import { WebsiteIcon } from "@/app/icons/site"
import { XIcon } from "@/app/icons/x"
import { YouTubeIcon } from "@/app/icons/youtube"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { Button, Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { useMolecule } from "bunshi/react"
import { useEffect, useRef, useState } from "react"

interface BannerSectionProps {
  mediaType: "image" | "video"
  mediaUrl: string
  // For video, support both single URL and separate desktop/mobile
  desktopVideoUrl?: string
  mobileVideoUrl?: string
  title?: string
  description?: string
  logo?: string
  socialLinks?: {
    website?: string
    twitter?: string
    discord?: string
    instagram?: string
    youtube?: string
  }
  stats?: {
    totalCollections?: number
    totalItems?: number
    totalVolume?: string
    percentListed?: string
  }
  statsLoading?: boolean
  statsError?: Error | null
  autoplay?: boolean
  muted?: boolean
  loop?: boolean
  controls?: boolean
  ctaButton?: {
    text: string
    url: string
  }
}

interface MerchantData {
  name?: string
  description?: string
  logo?: string
  website?: string
  twitter?: string
  discord?: string
  instagram?: string
  youtube?: string
}

interface BannerSectionComponentProps {
  props: BannerSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
  merchantData?: MerchantData
}

export function BannerSection({
  props,
  styles,
  merchantData,
}: BannerSectionComponentProps) {
  const logger = useMolecule(LoggerMolecule)

  const {
    mediaType,
    mediaUrl,
    desktopVideoUrl,
    mobileVideoUrl,
    title,
    description,
    logo,
    socialLinks,
    stats,
    ctaButton,
    autoplay = true,
    muted = true,
    loop = true,
    controls = false,
  } = props

  // Fallback logic: Use merchant data when props are not provided
  const displayLogo = logo || merchantData?.logo
  const displayTitle = title // No fallback for title
  const displayDescription = description || merchantData?.description

  // Merge social links with merchant data as fallback
  const displaySocialLinks =
    socialLinks || merchantData
      ? {
          website: socialLinks?.website || merchantData?.website,
          twitter: socialLinks?.twitter || merchantData?.twitter,
          discord: socialLinks?.discord || merchantData?.discord,
          instagram: socialLinks?.instagram || merchantData?.instagram,
          youtube: socialLinks?.youtube || merchantData?.youtube,
        }
      : undefined

  // Get brand background color for fade effect
  const brandBgColor = styles?.backgroundColor || "#8B5CF6"

  const [isMobile, setIsMobile] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const bannerContext: EventContext = {
      sectionType: "banner",
      mediaType,
      hasTitle: (!!displayTitle).toString(),
      hasDescription: (!!displayDescription).toString(),
      hasLogo: (!!displayLogo).toString(),
      hasStats: (!!stats).toString(),
      hasSocialLinks: (!!displaySocialLinks).toString(),
      hasCtaButton: (!!ctaButton).toString(),
      autoplay: autoplay.toString(),
      muted: muted.toString(),
      loop: loop.toString(),
      controls: controls.toString(),
      flow: Flow.BrandPage,
    }

    logger?.api.pushEvent("Brand banner section viewed", bannerContext)
    amplitude.track("Brand banner section viewed", bannerContext)
  }, [
    mediaType,
    displayTitle,
    displayDescription,
    displayLogo,
    stats,
    displaySocialLinks,
    ctaButton,
    autoplay,
    muted,
    loop,
    controls,
    logger,
  ])

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Handle video autoplay on mobile (some browsers require user interaction)
  useEffect(() => {
    if (videoRef.current && autoplay && mediaType === "video") {
      const playVideo = async () => {
        try {
          await videoRef.current?.play()
        } catch (error) {
          console.error("Autoplay prevented:", error)
        }
      }
      playVideo()
    }
  }, [autoplay, mediaType])

  // Determine which video URL to use
  const currentVideoUrl =
    mediaType === "video"
      ? desktopVideoUrl && mobileVideoUrl
        ? isMobile
          ? mobileVideoUrl
          : desktopVideoUrl
        : mediaUrl
      : mediaUrl

  return (
    <div
      style={{
        backgroundColor: styles?.backgroundColor,
        color: styles?.textColor,
        padding: styles?.padding,
        margin: styles?.margin,
      }}
    >
      {/* Banner Container */}
      <BannerContainer>
        {/* Media Background */}
        {mediaType === "video" ? (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            src={currentVideoUrl}
            autoPlay={autoplay}
            muted={muted}
            loop={loop}
            controls={controls}
            playsInline // Important for mobile Safari
            preload="metadata"
            onError={(e) => {
              console.error("Video error:", e)
            }}
            onLoadStart={() => {}}
          >
            <source src={currentVideoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={mediaUrl}
            alt={title || "Banner"}
            className="h-full w-full object-cover"
            onError={(e) => {
              console.error("Image failed to load:", mediaUrl)
            }}
          />
        )}

        {/* Gradient overlay for fade effect with brand color */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, transparent 30%, ${brandBgColor}66 60%, ${brandBgColor}CC 80%, ${brandBgColor} 100%)`,
          }}
        />

        {/* Content overlay */}
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          {/* Bottom section - Logo, Description, Social Links, and Stats */}
          <div className="flex flex-col gap-6">
            {/* Social Links and Stats Row */}
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
              {/* Logo and Description */}
              <div className="flex flex-col">
                {/* Show logo (with merchant fallback) instead of title */}
                {displayLogo && (
                  <div className="mb-4">
                    <img
                      src={displayLogo}
                      alt="Brand logo"
                      className="h-auto max-h-12 w-auto md:max-h-16"
                    />
                  </div>
                )}
                {/* Fallback to title if no logo */}
                {!displayLogo && displayTitle && (
                  <h1 className="font-bold text-2xl text-white leading-tight md:text-3xl">
                    {displayTitle}
                  </h1>
                )}
                {/* Show description (with merchant fallback) */}
                {displayDescription && (
                  <p className="max-w-[580px] text-sm text-white/70 md:text-base mb-4">
                    {displayDescription}
                  </p>
                )}
                {/* Left side - Social Links (with merchant fallback) */}
                {displaySocialLinks && (
                  <SocialLinks socialLinks={displaySocialLinks} />
                )}
              </div>

              {/* Right side - Stats */}
              <BannerStats
                stats={stats}
                isLoading={props.statsLoading}
                error={props.statsError}
              />
            </div>
          </div>
        </div>
      </BannerContainer>
    </div>
  )
}

// Banner container with responsive aspect ratio
function BannerContainer({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden",
        // Taller height to match the design
        "h-[400px]", // Fixed height for mobile
        "md:h-[500px]", // Fixed height for desktop
        className,
      )}
    >
      {children}
    </div>
  )
}

// Social Links Component
function SocialLinks({
  socialLinks,
}: { socialLinks: NonNullable<BannerSectionProps["socialLinks"]> }) {
  // Get logger for event tracking
  const logger = useMolecule(LoggerMolecule)

  const links = [
    {
      key: "discord",
      url: socialLinks.discord,
      icon: DiscordIcon,
      label: "Discord",
    },
    { key: "twitter", url: socialLinks.twitter, icon: XIcon, label: "Twitter" },
    {
      key: "instagram",
      url: socialLinks.instagram,
      icon: InstagramIcon,
      label: "Instagram",
    },
  ].filter((link) => link.url)

  // Track social link clicks
  const handleSocialClick = (platform: string, url: string) => {
    const socialContext: EventContext = {
      platform,
      url,
      flow: Flow.BrandPage,
      sectionType: "banner",
    }

    logger?.api.pushEvent("Brand social link clicked", socialContext)
    amplitude.track("Brand social link clicked", socialContext)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: document.title,
        text: "Check this out",
        url: window.location.href,
      })
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-4 md:gap-6">
      {/* Social Icons */}
      {links.map(({ key, url, icon: Icon, label }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="transition-opacity hover:opacity-70"
          aria-label={label}
          onClick={() => handleSocialClick(key, url || "")}
        >
          <Icon className="h-5 w-5 text-white md:h-6 md:w-6" />
        </a>
      ))}

      {/* Share Button */}
      <button
        type="button"
        onClick={handleShare}
        className="flex items-center gap-1.5 transition-opacity hover:opacity-70 md:gap-2"
        aria-label="Share"
      >
        <svg
          className="h-4 w-4 text-white md:h-5 md:w-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92S19.61 16.08 18 16.08z" />
        </svg>
        <span className="text-white text-xs md:text-sm">Share</span>
      </button>
    </div>
  )
}

// Banner Stats Component
function BannerStats({
  stats,
  isLoading,
  error,
}: {
  stats?: BannerSectionProps["stats"]
  isLoading?: boolean
  error?: Error | null
}) {
  // Show loading skeletons
  if (isLoading) {
    return (
      <div className="flex items-end gap-4 md:gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-1 text-right">
            <Skeleton className="ml-auto h-4 w-20" />
            <Skeleton className="ml-auto h-8 w-24" />
          </div>
        ))}
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col gap-6 text-right">
        <div className="text-muted-foreground text-sm">
          Unable to load statistics
        </div>
      </div>
    )
  }

  // Show stats if available
  if (!stats) {
    return null
  }

  const statItems = [
    {
      label: "Global Volume",
      value: stats.totalVolume
        ? stats.totalVolume.startsWith("$")
          ? stats.totalVolume
          : `$${stats.totalVolume}`
        : undefined,
    },
    {
      label: "Unique Collectors",
      value: stats.totalItems?.toLocaleString(),
    },
    {
      label: "Items Listed",
      value: stats.percentListed,
    },
  ].filter((item) => item.value !== undefined) // Only show items with actual data

  return (
    <div className="flex items-end gap-4 md:gap-8">
      {statItems.map(({ label, value }) => (
        <div key={label} className="flex flex-col text-right">
          <span className="text-white/70 text-xs">{label}</span>
          <span className="font-bold text-lg text-white md:text-2xl">
            {value}
          </span>
        </div>
      ))}
    </div>
  )
}
